import React, { useState } from 'react';
import { Box, Button, Flex, Collapsible, Text } from '@chakra-ui/react';
import { AxiosError } from 'axios';
import * as Sentry from '@sentry/react';
import { useUpdateUserEmail } from 'hooks/api/backend/user/updateUserData';
import { useNovaToast } from 'hooks/utils/useNovaToast';
import { useUser } from 'utils/auth';
import { toastContent } from 'config/toast';
import { axios } from 'utils/axios';
import { handleMutationError } from 'utils/handleMutationError';
import { ArrowCircleIcon, EmailIcon } from 'assets/icons/v2';
import { NovaIcon } from 'components/ui/novaIcon';
import Joi from 'joi';
import { ToastVariant } from 'types/Toast';
import { errorMessage } from './Form/ErrorMessages';
import { Form, RHFFormInput } from './Form/v2';

type EditUserEmailInputs = {
  newMail: string;
};

const emailSchema = Joi.object({
  newMail: Joi.string()
    .email({
      tlds: {
        allow: false,
      },
    })
    .required()
    .messages({
      'string.empty': errorMessage.empty,
      'string.email': errorMessage.email,
    }),
});

export const ChangeMailDropout = (): React.ReactElement => {
  const [isOpen, setIsOpen] = useState(false);
  const { mutateAsync: updateUserEmail } = useUpdateUserEmail(false);
  const toast = useNovaToast();
  const { refetch: refetchUser } = useUser();

  const onEmailSubmit = async (values: EditUserEmailInputs): Promise<void> => {
    if (!values.newMail) return;

    try {
      const isValid = await handleEmailValidationChangeSubmit(values.newMail);
      if (isValid) {
        const result = await updateUserEmail(values.newMail);
        if (result) {
          toast.show(toastContent.email.changeSuccess);
          await refetchUser();
        }
      }
      setIsOpen(false);
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError?.response?.status === 400 && axiosError?.response?.data === 'NOT_ALLOWED') {
        // Not allowed means you are using the social login
        toast.show(toastContent.email.notAllowed);
        return;
      }
      handleMutationError(error);
    }
  };

  const handleEmailValidationChangeSubmit = async (email: string): Promise<boolean> => {
    try {
      const validateMail = await axios.post('/auth/validate', {
        email,
      });

      if (validateMail.data === 'USED') {
        toast.show(toastContent.email.taken);
        return false;
      }
      return true;
    } catch (error) {
      toast.show({ ...toastContent.error.default, variant: ToastVariant.ERROR });
      Sentry.captureException(error);
      return false;
    }
  };

  return (
    <Box width="100%" maxW="300px">
      <Flex
        alignItems="center"
        justifyContent="center"
        onClick={() => setIsOpen(!isOpen)}
        cursor="pointer"
        gap="xs"
      >
        <NovaIcon
          icon={ArrowCircleIcon}
          size="m"
          color="icon.info"
          style={{ rotate: isOpen ? '0deg' : '180deg' }}
        />
        <Text color="text.info">E-Mail Adresse ändern</Text>
      </Flex>

      <Collapsible.Root open={isOpen}>
        <Collapsible.Content>
          <Flex mt="m" direction="column" gap="xs" justifyContent="center" alignItems="center">
            <Form<EditUserEmailInputs, typeof emailSchema>
              onSubmit={onEmailSubmit}
              schema={emailSchema}
              autoComplete="on"
              options={{
                defaultValues: { newMail: '' },
                reValidateMode: 'onChange',
              }}
            >
              <Flex flexDir="column" alignItems="center" gap="xs">
                <RHFFormInput
                  name="newMail"
                  type="email"
                  leftElement={
                    <NovaIcon icon={EmailIcon} size="s" color="icon.quaternary" aria-hidden />
                  }
                  placeholder="Neue E-Mail Adresse"
                />
                <Text textStyle="body.xs.regular" color="text.secondary" textAlign="center">
                  Die eingegebene E-Mail wird nach der Aktualisierung für die Anmeldung verwendet.
                </Text>
                <Button mt="xs" variant="secondaryOutline" type="submit" width="fit-content">
                  E-Mail ändern und senden
                </Button>
              </Flex>
            </Form>
          </Flex>
        </Collapsible.Content>
      </Collapsible.Root>
    </Box>
  );
};
